// src/admin/pages/AdminProducts.jsx
import React, { useState, useEffect } from "react";
import { imageService, productService } from "../../../services/api";
import { toast } from "react-toastify";
import TabBlockView from "../../components/TabBlockView";
import { loadSeo, getSeoPage, updateSeoPage } from "../../../backend/seo/seo";

// ─── SEO helpers ──────────────────────────────────────────────────────────────
const SEO_PAGE = "shop";
const SEO_KEYS = ["title", "meta", "h1", "description", "h2_1", "h2_2"] as const;
type SeoKey = (typeof SEO_KEYS)[number];

const SEO_LABELS: Record<SeoKey, string> = {
  title: "Title (тег <title>)",
  meta: "Meta description",
  h1: "H1",
  description: "Опис (між H1 та H2)",
  h2_1: "H2 (перший)",
  h2_2: "H2 (другий)",
};

const SEO_PLACEHOLDERS: Record<SeoKey, string> = {
  title: "Мерч — офіційний магазин",
  meta: "Опис сторінки для пошукових систем",
  h1: "Заголовок сторінки",
  description: "Текст опису між заголовком та підзаголовками...",
  h2_1: "Підзаголовок 1",
  h2_2: "Підзаголовок 2",
};

// ─── TagSelector ─────────────────────────────────────────────────────────────
/**
 * Two-panel tag selector:
 *   LEFT  — compact text input + "Add" button. Typing a name and pressing
 *            Enter / clicking the button adds it to the pool (session-only)
 *            and immediately selects it.
 *   RIGHT — the full pool rendered as toggle chips. Active ones are green.
 *
 * Both category and filters use this same component (always multi-select).
 */
const TagSelector = ({
  label,
  pool,
  selected,
  onChange,
  onAddToPool,
}: {
  label: string;
  pool: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  onAddToPool: (tag: string) => void;
}) => {
  const [inputValue, setInputValue] = useState("");

  const toggle = (tag: string) => {
    onChange(
      selected.includes(tag)
        ? selected.filter((t) => t !== tag)
        : [...selected, tag]
    );
  };

  const addNew = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    // Add to pool if not already there
    if (!pool.includes(trimmed)) onAddToPool(trimmed);
    // Select it
    if (!selected.includes(trimmed)) onChange([...selected, trimmed]);
    setInputValue("");
  };

  return (
    <div style={tagStyles.wrapper}>
      <label style={styles.label}>{label}:</label>

      <div style={tagStyles.twoPanel}>
        {/* ── Left: add-new input ── */}
        <div style={tagStyles.addPanel}>
          <p style={tagStyles.addPanelHint}>Нове значення</p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNew(); } }}
            placeholder="Введіть назву..."
            style={tagStyles.addInput}
          />
          <button
            type="button"
            onClick={addNew}
            disabled={!inputValue.trim()}
            style={{
              ...tagStyles.addBtn,
              opacity: inputValue.trim() ? 1 : 0.4,
              cursor: inputValue.trim() ? "pointer" : "default",
            }}
          >
            + Додати
          </button>
        </div>

        {/* ── Divider ── */}
        <div style={tagStyles.divider} />

        {/* ── Right: pool chips ── */}
        <div style={tagStyles.chipPanel}>
          {pool.length === 0 ? (
            <span style={tagStyles.emptyNote}>Додайте перше значення ліворуч →</span>
          ) : (
            pool.map((tag) => {
              const active = selected.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggle(tag)}
                  style={{
                    ...tagStyles.chip,
                    backgroundColor: active ? "#2e7d32" : "#f0f0f0",
                    color: active ? "#fff" : "#333",
                    border: active ? "1.5px solid #2e7d32" : "1.5px solid #ccc",
                  }}
                >
                  {tag}
                  {active && <span style={tagStyles.chipClose}> ✕</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminProducts = () => {
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [formMode, setFormMode]         = useState("add");
  const [formData, setFormData]         = useState({
    imageLink: "",
    name: "",
    price: "",
    shopLink: "",
    isVisible: true,
    categories: [] as string[],
    filters: [] as string[],
  });
  const [currentId, setCurrentId]       = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageEntries, setImageEntries] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging]     = useState(false);
  const [tempProducts, setTempProducts] = useState([]);

  // Pools for TagSelector — seeded from existing products, extended per-session
  const [categoryPool, setCategoryPool] = useState<string[]>([]);
  const [filterPool, setFilterPool]     = useState<string[]>([]);

  const addToPool = (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (tag: string) => setter((prev) => prev.includes(tag) ? prev : [...prev, tag].sort());

  // SEO state
  const [seoData, setSeoData] = useState<Record<SeoKey, string>>({
    title: "", meta: "", h1: "", description: "", h2_1: "", h2_2: "",
  });
  const [seoLoading, setSeoLoading] = useState(true);
  const [seoSaving, setSeoSaving]   = useState(false);

  // New image via URL input state
  const [newImageUrl, setNewImageUrl]   = useState("");
  const [newImageAlt, setNewImageAlt]   = useState("");

  useEffect(() => {
    fetchProducts();
    fetchSeo();
  }, []);

  // ── Derive pools directly from the loaded products list ───────────────────
  // Runs whenever `products` changes (initial load, after add/edit/delete).
  // No extra API calls needed — the data is already here.
  useEffect(() => {
    const cats = Array.from(
      new Set(
        products.flatMap((p: any) =>
          Array.isArray(p.categories) ? p.categories
          : p.category ? [p.category] : []
        )
      )
    ).sort();

    const fils = Array.from(
      new Set(
        products.flatMap((p: any) =>
          Array.isArray(p.filters) ? p.filters : []
        )
      )
    ).sort();

    setCategoryPool(cats);
    setFilterPool(fils);
  }, [products]);

  // ── SEO ────────────────────────────────────────────────────────────────────
  const fetchSeo = async () => {
    try {
      setSeoLoading(true);
      await loadSeo();
      const data = getSeoPage(SEO_PAGE);
      const merged: Record<SeoKey, string> = { title: "", meta: "", h1: "", description: "", h2_1: "", h2_2: "" };
      SEO_KEYS.forEach((key) => { if (data[key] !== undefined) merged[key] = data[key]; });
      setSeoData(merged);
    } catch (err) {
      console.error("Error fetching SEO:", err);
      toast.error("Не вдалося завантажити SEO дані");
    } finally {
      setSeoLoading(false);
    }
  };

  const handleSeoChange = (key: SeoKey, value: string) => {
    setSeoData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSeoSubmit = async (e) => {
    e.preventDefault();
    setSeoSaving(true);
    try {
      await updateSeoPage(SEO_PAGE, seoData);
      toast.success("SEO успішно збережено!");
    } catch (err) {
      console.error("Error saving SEO:", err);
      toast.error("Помилка при збереженні SEO");
    } finally {
      setSeoSaving(false);
    }
  };

  // ── Image helpers ──────────────────────────────────────────────────────────
  const parseImageLink = (imageLink) => {
    if (!imageLink || imageLink.trim() === "") return [];
    return imageLink.split(",").map((entry) => {
      const trimmed = entry.trim();
      const parts = trimmed.split("|");
      return { link: parts[0] || "", alt: parts[1] || "" };
    });
  };

  const stringifyImageEntries = (entries) =>
    entries
      .filter((entry) => entry.link.trim())
      .map((entry) => `${entry.link}|${entry.alt || ""}`)
      .join(", ");

  useEffect(() => {
    setImageEntries(parseImageLink(formData.imageLink));
  }, []);

  const handleImageEntryChange = (index, field, value) => {
    const updated = [...imageEntries];
    updated[index] = { ...updated[index], [field]: value };
    setImageEntries(updated);
  };

  const removeImageEntry = (index) => {
    setImageEntries(imageEntries.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    try {
      setUploadingImage(true);
      const uploadedLinks = await Promise.all(
        files.map(async (file: any) => {
          const link = `products/${Date.now()}_${file.name}`;
          return await imageService.upload(file, link);
        }),
      );
      setImageEntries([...imageEntries, ...uploadedLinks.map((link) => ({ link, alt: "" }))]);
      toast.success("Зображення успішно завантажено!");
    } catch (err) {
      console.error("Error uploading images:", err);
      toast.error("Помилка при завантаженні зображень");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleAddImageByUrl = () => {
    const trimmed = newImageUrl.trim();
    if (!trimmed) return;
    setImageEntries([...imageEntries, { link: trimmed, alt: newImageAlt.trim() }]);
    setNewImageUrl("");
    setNewImageAlt("");
  };

  const handleAddPhotoClick = () => document.getElementById("addPhotoInput")?.click();

  // ── Products CRUD ──────────────────────────────────────────────────────────
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Не вдалося завантажити продукти");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : name === "price" ? parseFloat(value) || 0 : value,
    });
  };

  const resetForm = () => {
    setFormData({ imageLink: "", name: "", price: "", shopLink: "", isVisible: true, categories: [], filters: [] });
    setFormMode("add");
    setCurrentId(null);
    setImageEntries([]);
    setNewImageUrl("");
    setNewImageAlt("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const finalFormData = {
        ...formData,
        imageLink: stringifyImageEntries(imageEntries),
        // Both categories and filters are string[] — API serializes to CSV
        categories: formData.categories,
        filters: formData.filters,
      };

      if (formMode === "add") {
        await productService.create(finalFormData);
        toast.success("Продукт успішно додано!");
      } else {
        await productService.update(currentId, finalFormData);
        toast.success("Продукт успішно оновлено!");
      }
      resetForm();
      await fetchProducts();
    } catch (err) {
      console.error("Error saving product:", err);
      toast.error(err.message || `Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} продукту`);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      imageLink: product.imageLink,
      name: product.name,
      price: product.price,
      shopLink: product.shopLink,
      isVisible: product.isVisible,
      categories: Array.isArray(product.categories) ? product.categories
                  : product.category ? [product.category] : [],
      filters: Array.isArray(product.filters) ? product.filters : [],
    });
    setCurrentId(product.id);
    setFormMode("edit");
    setImageEntries(parseImageLink(product.imageLink));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цей продукт?")) return;
    try {
      await productService.delete(id);
      setProducts(products.filter((item) => item.id !== id));
      toast.success("Продукт успішно видалено!");
    } catch (err) {
      toast.error("Помилка при видаленні продукту");
    }
  };

  const toggleVisibility = async (product) => {
    try {
      const updated = { ...product, isVisible: !product.isVisible };
      await productService.update(product.id, updated);
      setProducts(products.map((p) => (p.id === product.id ? updated : p)));
      toast.success(`Продукт ${updated.isVisible ? "показано" : "приховано"}!`);
    } catch (err) {
      toast.error("Помилка при зміні видимості");
    }
  };

  const moveItemUp = async (index) => {
    if (index <= 0) return;
    try {
      const updated = [...products];
      const a = updated[index], b = updated[index - 1];
      const aid = a.id, bid = b.id;
      const ua = { ...a, id: bid }, ub = { ...b, id: aid };
      updated[index] = ub; updated[index - 1] = ua;
      setProducts(updated);
      await Promise.all([productService.update(bid, ua), productService.update(aid, ub)]);
      toast.success("Порядок успішно змінено!");
    } catch { fetchProducts(); toast.error("Помилка при зміні порядку"); }
  };

  const moveItemDown = async (index) => {
    if (index >= products.length - 1) return;
    try {
      const updated = [...products];
      const a = updated[index], b = updated[index + 1];
      const aid = a.id, bid = b.id;
      const ua = { ...a, id: bid }, ub = { ...b, id: aid };
      updated[index] = ub; updated[index + 1] = ua;
      setProducts(updated);
      await Promise.all([productService.update(bid, ua), productService.update(aid, ub)]);
      toast.success("Порядок успішно змінено!");
    } catch { fetchProducts(); toast.error("Помилка при зміні порядку"); }
  };

  const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index); setDragOverIndex(index);
    setIsDragging(true); setTempProducts([...products]);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
    if (draggedIndex === null || dragOverIndex === index) return;
    setTempProducts(reorderArray(tempProducts, dragOverIndex, index));
    setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dragOverIndex) {
      setIsDragging(false); setDraggedIndex(null); setDragOverIndex(null); setTempProducts([]);
      return;
    }
    try {
      let current = [...products];
      const start = draggedIndex, end = dragOverIndex;
      if (start < end) {
        for (let i = start; i < end; i++) {
          const i1 = current[i], i2 = current[i + 1];
          const u1 = { ...i1, id: i2.id }, u2 = { ...i2, id: i1.id };
          current[i] = u2; current[i + 1] = u1;
          await Promise.all([productService.update(i2.id, u1), productService.update(i1.id, u2)]);
        }
      } else {
        for (let i = start; i > end; i--) {
          const i1 = current[i], i2 = current[i - 1];
          const u1 = { ...i1, id: i2.id }, u2 = { ...i2, id: i1.id };
          current[i] = u2; current[i - 1] = u1;
          await Promise.all([productService.update(i2.id, u1), productService.update(i1.id, u2)]);
        }
      }
      toast.success("Порядок успішно змінено!");
      await fetchProducts();
    } catch { toast.error("Помилка при зміні порядку"); fetchProducts(); }
    finally {
      setIsDragging(false); setDraggedIndex(null); setDragOverIndex(null); setTempProducts([]);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false); setDraggedIndex(null); setDragOverIndex(null); setTempProducts([]);
  };

  // ── Image Preview ──────────────────────────────────────────────────────────
  const ImagePreview = ({ imageLink }) => {
    const images = parseImageLink(imageLink);
    if (images.length === 0) return <div style={styles.noImagePlaceholder}>Немає зображення</div>;
    if (images.length === 1) return <img src={images[0].link} alt={images[0].alt} style={styles.productImage} />;
    return (
      <div style={styles.imageCarouselContainer}>
        <div style={styles.imageGrid}>
          {images.slice(0, 4).map((image, index) => (
            <img key={index} src={image.link} alt={image.alt} style={styles.gridImage} />
          ))}
          {images.length > 4 && <div style={styles.moreImagesOverlay}>+{images.length - 4}</div>}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Продукти</h1>
      <div style={styles.loadingContainer}><p>Завантаження...</p></div>
    </div>
  );

  if (error) return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Продукти</h1>
      <p>{error}</p>
    </div>
  );

  // ── SEO Tab ────────────────────────────────────────────────────────────────
  const seoTabContent = (
    <div style={styles.formContainer}>
      <h2 style={styles.formTitle}>SEO — сторінка магазину</h2>
      {seoLoading ? (
        <p>Завантаження SEO даних...</p>
      ) : (
        <form onSubmit={handleSeoSubmit} style={styles.seoForm}>
          {SEO_KEYS.map((key) => (
            <div key={key} style={styles.seoFormGroup}>
              <label style={styles.label} htmlFor={`seo_${key}`}>{SEO_LABELS[key]}:</label>
              {key === "meta" || key === "description" ? (
                <textarea
                  id={`seo_${key}`}
                  value={seoData[key]}
                  onChange={(e) => handleSeoChange(key, e.target.value)}
                  placeholder={SEO_PLACEHOLDERS[key]}
                  style={styles.textarea}
                  rows={key === "description" ? 4 : 3}
                />
              ) : (
                <input
                  id={`seo_${key}`}
                  type="text"
                  value={seoData[key]}
                  onChange={(e) => handleSeoChange(key, e.target.value)}
                  placeholder={SEO_PLACEHOLDERS[key]}
                  style={styles.input}
                />
              )}
              <span style={styles.seoHint}>
                {key === "title"       && "Відображається у вкладці браузера та результатах пошуку"}
                {key === "meta"        && "Короткий опис сторінки у результатах пошуку (до 160 символів)"}
                {key === "h1"          && "Головний заголовок сторінки (лише один на сторінці)"}
                {key === "description" && "Текст між H1 та H2, відображається на сторінці"}
                {key === "h2_1"        && "Перший підзаголовок другого рівня"}
                {key === "h2_2"        && "Другий підзаголовок другого рівня"}
              </span>
            </div>
          ))}
          <div style={styles.buttonContainer}>
            <button type="submit" style={styles.button} disabled={seoSaving}>
              {seoSaving ? "Збереження..." : "Зберегти SEO"}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  // ── Products Tab ───────────────────────────────────────────────────────────
  const productsTabContent = (
    <div>
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати новий продукт" : "Редагувати продукт"}
        </h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Name */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="name">Назва:</label>
            <input style={styles.input} type="text" id="name" name="name"
              placeholder="Назва продукту" value={formData.name}
              onChange={handleInputChange} required />
          </div>

          {/* Price */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="price">Ціна:</label>
            <input style={styles.input} type="number" id="price" name="price"
              placeholder="0.00" value={formData.price} onChange={handleInputChange}
              step="0.01" min="0" required />
          </div>

          {/* Visible */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input type="checkbox" name="isVisible" checked={formData.isVisible}
                onChange={handleInputChange} style={styles.checkbox} />
              Показувати продукт
            </label>
          </div>

          {/* ── Categories (multi-select TagSelector) ── */}
          <div style={{ ...styles.formGroupFullWidth }}>
            <TagSelector
              label="Категорії"
              pool={categoryPool}
              selected={formData.categories}
              onChange={(next) => setFormData((prev) => ({ ...prev, categories: next }))}
              onAddToPool={addToPool(setCategoryPool)}
            />
          </div>

          {/* ── Filters (multi-select TagSelector) ── */}
          <div style={styles.formGroupFullWidth}>
            <TagSelector
              label="Фільтри"
              pool={filterPool}
              selected={formData.filters}
              onChange={(next) => setFormData((prev) => ({ ...prev, filters: next }))}
              onAddToPool={addToPool(setFilterPool)}
            />
          </div>

          {/* ── Image Manager ── */}
          <div style={styles.formGroupFullWidth}>
            <label style={styles.label}>Зображення продукту:</label>
            <div style={styles.imageManager}>
              <div style={styles.imageEntriesContainer}>
                {imageEntries.map((entry, index) => (
                  <div key={index} style={styles.imageEntryCard}>
                    <div
                      style={styles.imagePreviewContainer}
                      onMouseEnter={(e) => {
                        const overlay: any = e.currentTarget.querySelector("[data-delete-overlay]");
                        if (overlay) overlay.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        const overlay: any = e.currentTarget.querySelector("[data-delete-overlay]");
                        if (overlay) overlay.style.opacity = "0";
                      }}
                    >
                      {entry.link ? (
                        <img src={entry.link} alt={entry.alt} style={styles.imagePreview} />
                      ) : (
                        <div style={styles.imagePreviewPlaceholder}><span>🖼️</span></div>
                      )}
                      <div data-delete-overlay style={styles.deleteOverlay}
                        onClick={() => removeImageEntry(index)}>
                        <span style={styles.deleteOverlayText}>Видалити</span>
                      </div>
                    </div>
                    <input type="text" placeholder="URL зображення" value={entry.link}
                      onChange={(e) => handleImageEntryChange(index, "link", e.target.value)}
                      style={styles.imageUrlInput} />
                    <input type="text" placeholder="Alt текст (опис)" value={entry.alt}
                      onChange={(e) => handleImageEntryChange(index, "alt", e.target.value)}
                      style={styles.imageAltInput} />
                  </div>
                ))}

                <input type="file" id="addPhotoInput" accept="image/*" multiple
                  onChange={handleFileUpload} style={styles.fileInputHidden}
                  disabled={uploadingImage} />

                <div style={styles.addPhotoCard}>
                  <div style={styles.addPhotoSection} onClick={handleAddPhotoClick}
                    title="Завантажити з комп'ютера">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                      strokeLinejoin="round">
                      <polyline points="16 16 12 12 8 16"></polyline>
                      <line x1="12" y1="12" x2="12" y2="21"></line>
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
                    </svg>
                    <span style={styles.addPhotoText}>
                      {uploadingImage ? "Завантаження..." : "Завантажити"}
                    </span>
                  </div>
                  <div style={styles.addPhotoDivider}>або</div>
                  <div style={styles.addPhotoUrlSection}>
                    <input type="text" placeholder="Вставити URL..."
                      value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddImageByUrl())}
                      style={styles.addPhotoUrlInput} />
                    <input type="text" placeholder="Alt текст"
                      value={newImageAlt} onChange={(e) => setNewImageAlt(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddImageByUrl())}
                      style={styles.addPhotoUrlInput} />
                    <button type="button" onClick={handleAddImageByUrl}
                      disabled={!newImageUrl.trim()}
                      style={{
                        ...styles.addUrlButton,
                        opacity: newImageUrl.trim() ? 1 : 0.4,
                        cursor: newImageUrl.trim() ? "pointer" : "default",
                      }}>
                      Додати
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shop link */}
          <div style={styles.formGroupFullWidth}>
            <label style={styles.label} htmlFor="shopLink">Посилання на магазин:</label>
            <input style={styles.input} type="text" id="shopLink" name="shopLink"
              placeholder="https://shop.example.com/product" value={formData.shopLink}
              onChange={handleInputChange} required />
          </div>

          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>
                Скасувати
              </button>
            )}
            <button type="submit" style={styles.button} disabled={uploadingImage}>
              {uploadingImage ? "Завантаження..." : formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      {/* ── Table ── */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Порядок</th>
              <th style={styles.tableHeader}>Зображення</th>
              <th style={styles.tableHeader}>Назва</th>
              <th style={styles.tableHeader}>Категорія / Фільтри</th>
              <th style={styles.tableHeader}>Ціна</th>
              <th style={styles.tableHeader}>Видимість</th>
              <th style={styles.tableHeader}>Посилання на магазин</th>
              <th style={styles.tableHeader}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center" }}>Немає доданих продуктів</td>
              </tr>
            ) : (
              (isDragging ? tempProducts : products).map((item, index) => {
                const isBeingDragged = isDragging && dragOverIndex === index && draggedIndex !== null;
                return (
                  <tr key={item.id} draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{
                      ...styles.tableRow,
                      opacity: item.isVisible == false ? 0.6 : isBeingDragged ? 0.4 : 1,
                      backgroundColor: isBeingDragged ? "#f0f0f0" : "transparent",
                      cursor: "move",
                      transform: isBeingDragged ? "scale(1.02)" : "scale(1)",
                      transition: "all 0.2s ease",
                      boxShadow: isBeingDragged ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
                    }}
                  >
                    <td style={styles.tableCell} data-label="Позиція">
                      <div style={styles.positionControlsContainer}>
                        <div style={styles.dragHandle} title="Перетягніть для зміни порядку">⋮⋮</div>
                        <div style={styles.positionButtonsContainer}>
                          <button style={styles.positionButton}
                            onClick={() => moveItemUp(products.findIndex((p) => p.id === item.id))}
                            disabled={products.findIndex((p) => p.id === item.id) === 0 || isDragging}
                            title="Перемістити вгору">▲</button>
                          <button style={styles.positionButton}
                            onClick={() => moveItemDown(products.findIndex((p) => p.id === item.id))}
                            disabled={products.findIndex((p) => p.id === item.id) === products.length - 1 || isDragging}
                            title="Перемістити вниз">▼</button>
                        </div>
                      </div>
                    </td>
                    <td style={styles.tableCell} data-label="Зображення">
                      <ImagePreview imageLink={item.imageLink} />
                    </td>
                    <td style={styles.tableCell} data-label="Назва">{item.name}</td>

                    {/* ── Categories + Filters column ── */}
                    <td style={styles.tableCell} data-label="Категорії / Фільтри">
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                        {Array.isArray(item.categories) && item.categories.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                            {item.categories.map((c) => (
                              <span key={c} style={tableCellStyles.categoryBadge}>{c}</span>
                            ))}
                          </div>
                        ) : item.category ? (
                          // backwards-compat: old single-category field
                          <span style={tableCellStyles.categoryBadge}>{item.category}</span>
                        ) : (
                          <span style={tableCellStyles.noBadge}>—</span>
                        )}
                        {Array.isArray(item.filters) && item.filters.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                            {item.filters.map((f) => (
                              <span key={f} style={tableCellStyles.filterBadge}>{f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>

                    <td style={styles.tableCell} data-label="Ціна">{item.price} грн</td>
                    <td style={styles.tableCell} data-label="Видимість">
                      <button
                        style={{ ...styles.visibilityButton, backgroundColor: item.isVisible != false ? "#4CAF50" : "#f44336" }}
                        onClick={() => toggleVisibility(item)}
                        title={item.isVisible != false ? "Приховати" : "Показати"}>
                        {item.isVisible != false ? "👁️" : "🚫"}
                      </button>
                    </td>
                    <td style={styles.tableCell} data-label="Посилання">
                      <a href={item.shopLink} target="_blank" rel="noopener noreferrer"
                        style={styles.shopLink} title={item.shopLink}>
                        {item.shopLink.length > 40 ? item.shopLink.substring(0, 40) + "..." : item.shopLink}
                      </a>
                    </td>
                    <td style={styles.tableCell} data-label="Дії">
                      <a style={styles.actionLink} onClick={() => handleEdit(item)}>Редагувати</a>
                      <a style={styles.actionLinkDanger} onClick={() => handleDelete(item.id)}>Видалити</a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Продукти</h1>
      <TabBlockView
        tabs={[
          { name: "Продукти", content: productsTabContent },
          { name: "SEO", content: seoTabContent },
        ]}
      />
    </div>
  );
};

// ─── TagSelector Styles ───────────────────────────────────────────────────────
const tagStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  twoPanel: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: 0,
    border: "1px solid #ddd",
    borderRadius: "6px",
    overflow: "hidden",
    backgroundColor: "#eee",
    minHeight: "80px",
  },
  // ── Left: add input ──
  addPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    padding: "0.75rem",
    width: "180px",
    flexShrink: 0,
    backgroundColor: "#e8e8e8",
    borderRight: "1px solid #ddd",
  },
  addPanelHint: {
    fontSize: "0.73rem",
    color: "#888",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    margin: 0,
  },
  addInput: {
    padding: "0.4rem 0.6rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.88rem",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  addBtn: {
    padding: "0.35rem 0.6rem",
    backgroundColor: "#2e7d32",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontSize: "0.82rem",
    fontWeight: 600,
    transition: "opacity 0.15s",
    textAlign: "center" as const,
  },
  divider: {
    width: 0,
    flexShrink: 0,
  },
  // ── Right: chip pool ──
  chipPanel: {
    flex: 1,
    display: "flex",
    flexWrap: "wrap" as const,
    alignContent: "flex-start",
    gap: "0.4rem",
    padding: "0.75rem",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.3rem 0.65rem",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontFamily: "'UAFSans', sans-serif",
    cursor: "pointer",
    transition: "all 0.15s",
    fontWeight: 500,
  },
  chipClose: {
    fontSize: "0.7rem",
    opacity: 0.8,
    marginLeft: "0.2rem",
  },
  emptyNote: {
    fontSize: "0.8rem",
    color: "#bbb",
    fontStyle: "italic",
    alignSelf: "center",
  },
};

// ─── Table cell badge styles ──────────────────────────────────────────────────
const tableCellStyles: Record<string, React.CSSProperties> = {
  categoryBadge: {
    display: "inline-block",
    padding: "0.2rem 0.55rem",
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    borderRadius: "10px",
    fontSize: "0.78rem",
    fontWeight: 600,
    fontFamily: "'UAFSans', sans-serif",
    whiteSpace: "nowrap",
  },
  filterBadge: {
    display: "inline-block",
    padding: "0.15rem 0.45rem",
    backgroundColor: "#f3e5f5",
    color: "#6a1b9a",
    borderRadius: "10px",
    fontSize: "0.75rem",
    fontWeight: 500,
    fontFamily: "'UAFSans', sans-serif",
    whiteSpace: "nowrap",
  },
  noBadge: {
    color: "#bbb",
    fontSize: "0.85rem",
  },
};

// ─── Page Styles ──────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  seoForm: { display: "flex", flexDirection: "column", gap: "1.25rem" },
  seoFormGroup: { display: "flex", flexDirection: "column" },
  seoHint: { marginTop: "0.25rem", fontSize: "0.78rem", color: "#888", fontStyle: "italic" },
  textarea: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
    resize: "vertical",
  },
  formGroup: { display: "flex", flexDirection: "column" },
  formGroupFullWidth: { display: "flex", flexDirection: "column", gridColumn: "1 / -1" },
  label: { marginBottom: "0.5rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.5rem" },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  checkbox: { marginRight: "0.5rem", transform: "scale(1.2)" },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: { display: "flex", justifyContent: "flex-end", gridColumn: "1 / -1" },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeader: {
    padding: "0.8rem",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    backgroundColor: "#e8e8e8",
    fontWeight: 600,
  },
  tableRow: { borderBottom: "1px solid #eee" },
  tableCell: { padding: "0.8rem", textAlign: "left", borderBottom: "1px solid #eee" },
  actionLink: { color: "var(--primary-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  actionLinkDanger: { color: "var(--error-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" },
  positionButtonsContainer: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  positionControlsContainer: { display: "flex", alignItems: "center", gap: "0.5rem" },
  dragHandle: {
    fontSize: "1.2rem", color: "#999", cursor: "grab", userSelect: "none",
    padding: "0.25rem", lineHeight: 1, letterSpacing: "-2px",
  },
  positionButton: {
    width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center",
    backgroundColor: "#f0f0f0", border: "1px solid #ddd", borderRadius: "4px",
    cursor: "pointer", fontSize: "12px", padding: 0, lineHeight: 1,
  },
  productImage: { width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #eee" },
  shopLink: { color: "var(--primary-color)", textDecoration: "none", wordBreak: "break-all", fontSize: "0.85rem" },
  visibilityButton: {
    width: "36px", height: "36px", display: "flex", alignItems: "center",
    justifyContent: "center", color: "white", border: "none", borderRadius: "4px",
    cursor: "pointer", fontSize: "16px", fontWeight: 500, padding: 0,
  },
  imageCarouselContainer: { position: "relative" },
  imageGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px", width: "60px", height: "60px", position: "relative" },
  gridImage: { width: "29px", height: "29px", objectFit: "cover", borderRadius: "2px" },
  moreImagesOverlay: {
    position: "absolute", bottom: "0", right: "0", width: "29px", height: "29px",
    backgroundColor: "rgba(0, 0, 0, 0.7)", color: "white", display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", borderRadius: "2px",
  },
  noImagePlaceholder: {
    width: "60px", height: "60px", display: "flex", alignItems: "center", justifyContent: "center",
    backgroundColor: "#f0f0f0", color: "#666", fontSize: "10px",
    border: "1px dashed #ccc", borderRadius: "4px", textAlign: "center",
  },
  imageManager: { marginTop: "0.5rem" },
  imageEntriesContainer: { display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" },
  imageEntryCard: {
    display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.75rem",
    backgroundColor: "white", border: "1px solid #ddd", borderRadius: "8px", width: "180px",
  },
  imagePreviewContainer: { position: "relative", width: "100%", height: "150px", backgroundColor: "#f5f5f5", borderRadius: "4px", overflow: "hidden" },
  imagePreview: { width: "100%", height: "100%", objectFit: "cover" },
  imagePreviewPlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem", color: "#ccc" },
  deleteOverlay: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)", display: "flex", alignItems: "center",
    justifyContent: "center", opacity: 0, transition: "opacity 0.2s", cursor: "pointer",
  },
  deleteOverlayText: {
    backgroundColor: "white", color: "#333", padding: "0.5rem 1rem",
    borderRadius: "4px", fontSize: "0.9rem", fontWeight: 500, fontFamily: "'UAFSans', sans-serif",
  },
  imageUrlInput: { padding: "0.5rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.85rem", fontFamily: "'UAFSans', sans-serif", outline: "none" },
  imageAltInput: { padding: "0.5rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.85rem", fontFamily: "'UAFSans', sans-serif", outline: "none" },
  addPhotoCard: {
    display: "flex", flexDirection: "column", padding: "0.75rem", width: "180px",
    border: "2px dashed #ccc", borderRadius: "8px", backgroundColor: "#fafafa",
    gap: "0.5rem", boxSizing: "border-box",
  },
  addPhotoSection: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: "0.35rem",
    color: "#555", cursor: "pointer", padding: "0.4rem 0", borderRadius: "6px", transition: "background 0.15s",
  },
  addPhotoText: { fontSize: "0.85rem", fontWeight: 500, fontFamily: "'UAFSans', sans-serif" },
  addPhotoDivider: {
    textAlign: "center", fontSize: "0.75rem", color: "#aaa",
    borderTop: "1px solid #e5e5e5", paddingTop: "0.4rem", marginTop: "0.1rem",
  },
  addPhotoUrlSection: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  addPhotoUrlInput: {
    padding: "0.4rem 0.5rem", border: "1px solid #ddd", borderRadius: "4px",
    fontSize: "0.8rem", fontFamily: "'UAFSans', sans-serif", outline: "none",
    width: "100%", boxSizing: "border-box",
  },
  addUrlButton: {
    padding: "0.4rem", backgroundColor: "green", color: "white", border: "none",
    borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.82rem",
    fontWeight: 500, transition: "opacity 0.2s",
  },
  fileInputHidden: { display: "none" },
};

export default AdminProducts;